import {
  classifyPurchaseError,
  createSubscribeFlow,
  withFailure,
  type PurchasePresenter,
  type SubscriptionServiceLevelStatus,
} from '@forest/paywall-core'
import { useMutation } from '@tanstack/react-query'
import {
  AbortError,
  getAuthenticatedUserInfo,
} from '~/lib/get-authenticated-user-info'
import { openIntercom } from '~/lib/intercom'
import { posthog } from '~/lib/posthog'
import { m } from '~/paraglide/messages'
import { language, platform } from '~/params'
import { native, type Product } from '@forest/native-services'
import { api } from '~/services/api'
import {
  $debugAfterPaymentError,
  $debugAfterPaymentErrorSilent,
} from '~/store/debug'
import { $receiptSystemAccountId } from '~/store/receipt-system-account-id'
import { $reminderLeadDays } from '~/store/reminder-lead-days'
import { dialog } from '~/store/dialog'
import { grantPermissionDialog } from '~/store/grant-permission-dialog'
import { currentTrialReminderGroup } from '~/features/multi-page/hooks/use-trial-reminder-group'
import {
  DAY_MS,
  runTrialReminderFlow,
  type TrialReminderDeps,
  type TrialReminderInput,
} from '~/features/multi-page/lib/trial-reminder'
import { runRestore } from '~/features/multi-page/lib/restore'
import { showSubscriptionConflictDialog } from '~/features/multi-page/lib/subscription-conflict-dialog'
import { showPurchaseConfirmationDialog } from '~/features/multi-page/lib/purchase-confirmation-dialog'

const trialReminderDeps: TrialReminderDeps = {
  hasNotificationPermission: () =>
    native.permission.getHasNotificationPermission(),
  requestNotificationPermission: () =>
    native.permission.requestNotificationPermission(),
  openReminderDialog: (reminderDate) =>
    grantPermissionDialog.open(reminderDate),
  scheduleNotification: (payload) => native.notification.schedule(payload),
  captureException: (error) => posthog?.captureException(error),
}

/**
 * The experiment state at purchase time: the arm and the lead time chosen on
 * Variant1's page (in-memory only, so a reload leaves it `null`). Read when
 * the purchase resolves, not at module load, because both can change while
 * the paywall is open.
 */
function trialReminderInputAtPurchase(): Pick<
  TrialReminderInput,
  'group' | 'reminderLeadDays' | 'locale'
> {
  return {
    group: currentTrialReminderGroup(),
    reminderLeadDays: $reminderLeadDays.get(),
    locale: language,
  }
}

const flow = createSubscribeFlow<Product>({
  api,
  platform,
  native: {
    purchase: native.iap.purchase,
    closeTransaction: native.iap.closeTransaction,
    getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
    getAllEntitlements: native.iap.getAllEntitlements,
    logEvent: native.general.logEvent,
    refreshServiceLevel: () => native.user.refreshServiceLevel(),
  },
  telemetry: {
    captureException: (error, context) =>
      posthog?.captureException(error, context),
  },
  getUserId: async () => (await getAuthenticatedUserInfo()).user_id,
  getReceiptSystemAccountId: () => $receiptSystemAccountId.get(),
  getDebugSimulate: () =>
    $debugAfterPaymentError.get() || $debugAfterPaymentErrorSilent.get(),
})

const presenter: PurchasePresenter<Product> = {
  success: async (facts) => {
    await runTrialReminderFlow(
      {
        ...trialReminderInputAtPurchase(),
        isTrialSuccess: facts.isTrialSuccess,
        expiresAt: facts.expiresAt,
      },
      trialReminderDeps,
    )
  },
  error: (intent, { error }) => {
    switch (intent.kind) {
      case 'payment_timeout':
        dialog.open({
          title: m.dialog_payment_timeout_title(),
          description: withFailure(
            m.dialog_payment_timeout_content({ error: intent.detail }),
            intent.failure,
          ),
          button: m.OK(),
        })
        return
      case 'illegal_transaction_restore':
        // QA-only: skip the auto restore dialog so the standalone "Restore
        // Purchases" button can be tested in isolation. The restorable store
        // transaction still exists, so the button has something to restore.
        if ($debugAfterPaymentErrorSilent.get()) return
        dialog.open({
          title: m.dialog_illegal_transaction_title(),
          description: withFailure(
            platform === 'china_android'
              ? m.dialog_illegal_transaction_content_cna()
              : m.dialog_illegal_transaction_content(),
            intent.failure,
          ),
          button: m.restore_purchase(),
          closeButton: true,
          asyncAction: async () => {
            dialog.setLoading(true)
            await runRestore()
          },
        })
        return
      case 'no_internet':
        dialog.open({
          title: m.dialog_no_internet_title(),
          description: withFailure(
            m.dialog_no_internet_content(),
            intent.failure,
          ),
          button: m.OK(),
        })
        return
      case 'http_error':
        dialog.open({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          button: m.contact_cs(),
          action: () => {
            openIntercom({
              node: 'subscribe_create_purchase_unexpected_http_error',
              orderId: intent.failure.purchaseId?.toString(),
              additional: { error_code: intent.failure.code },
            })
          },
        })
        return
      case 'confirmation_pending':
        showPurchaseConfirmationDialog(intent.failure)
        return
      case 'subscription_conflict':
        void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
        return
      case 'unknown':
        posthog?.captureException(error, {
          failure_code: intent.failure.code,
        })
        dialog.open({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          button: m.contact_cs(),
          action: () => {
            openIntercom({
              node: 'subscribe_create_purchase_unexpected_error',
              orderId: intent.failure.purchaseId?.toString(),
              additional: { error_code: intent.failure.code },
            })
          },
        })
        return
      default:
        intent satisfies never
    }
  },
}

export function useTrialMutation(params?: {
  onSuccess?: (
    serviceLevelStatus: SubscriptionServiceLevelStatus | null,
    product: Product,
  ) => Promise<unknown> | unknown
}) {
  const { onSuccess } = params ?? {}
  return useMutation({
    mutationFn: async (product: Product) => {
      if (import.meta.env.DEV) {
        // No native bridge locally: preview the reminder dialog through the
        // same flow with permission reported as missing, and log the schedule
        // payload Variant1 would send. A no-trial product (monthly no-trial)
        // previews the no-dialog path.
        const trialDays = product.eligible_trial_days
        await runTrialReminderFlow(
          {
            ...trialReminderInputAtPurchase(),
            isTrialSuccess: trialDays > 0,
            // As if the trial started now, so the previewed payload carries
            // a realistic expiry and a future fire time.
            expiresAt: new Date(Date.now() + trialDays * DAY_MS).toISOString(),
          },
          {
            ...trialReminderDeps,
            hasNotificationPermission: async () => ({
              success: true,
              data: false,
            }),
            requestNotificationPermission: async () => ({
              success: true,
              data: true,
            }),
            scheduleNotification: async (payload) => {
              console.log('[trial-reminder] schedule', payload)
              return {
                success: true,
                data: {
                  scheduled: payload.items.map(({ key, fire_at }) => ({
                    key,
                    fire_at,
                  })),
                },
              }
            },
          },
        )
        return null
      }
      return flow.run(product)
    },
    onSuccess: async (serviceLevelStatus, product) => {
      // The DEV branch above only previews the reminder dialog and never
      // purchases, so skip the real success side-effects there.
      if (!import.meta.env.DEV) {
        await presenter.success(flow.handleSuccess(serviceLevelStatus, product))
      }
      await onSuccess?.(serviceLevelStatus, product)
    },
    onError: (error) => {
      if (error instanceof AbortError) return
      presenter.error(classifyPurchaseError(error), { error, flow })
    },
  })
}
